"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { Badge, Button, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { Gavel as ModerationIcon } from "@mui/icons-material";
import { hasPermission } from "@/lib/admin/permissions";

export default function AdminModerationButton() {
  const { data: session } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isActive = pathname === "/admin/moderation";

  const hasModerationAccess = hasPermission(session, "moderation:view");

  const { data: pendingData } = useQuery({
    queryKey: ["admin", "moderation", "pending-count"],
    enabled: hasModerationAccess && !session?.impersonating,
    refetchInterval: 300000,
    refetchIntervalInBackground: true,
    queryFn: async () => {
      const res = await fetch("/api/admin/moderation/pending-count", {
        method: "GET",
        cache: "no-store",
      });
      if (!res.ok) {
        throw new Error("Failed to load moderation pending count");
      }
      return res.json();
    },
  });

  const pendingCount =
    typeof pendingData?.count === "number" ? pendingData.count : 0;

  if (!hasModerationAccess || session?.impersonating) return null;

  const navigateToModerationAdmin = () => {
    const event = new CustomEvent("meetingNavigationRequest", {
      detail: { href: "/admin/moderation" },
      cancelable: true,
      bubbles: false,
    });
    if (document.dispatchEvent(event)) {
      router.push("/admin/moderation");
    }
  };

  return (
    <>
      {isMobile ? (
        <Tooltip
          title={
            pendingCount > 0
              ? `Moderation (${pendingCount} case${
                  pendingCount === 1 ? "" : "s"
                } need attention)`
              : "Moderation"
          }
        >
          <IconButton
            color="inherit"
            onClick={navigateToModerationAdmin}
            size="small"
            sx={{ ml: 1 }}
          >
            <Badge
              badgeContent={pendingCount}
              color="error"
              invisible={pendingCount === 0}
              max={99}
            >
              <ModerationIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="text"
          color="inherit"
          onClick={navigateToModerationAdmin}
          startIcon={
            <Badge
              badgeContent={pendingCount}
              color="error"
              invisible={pendingCount === 0}
              max={99}
            >
              <ModerationIcon />
            </Badge>
          }
          sx={{
            mr: 2,
            color: theme.palette.text.secondary,
            fontWeight: isActive ? 700 : 400,
            maxWidth: 200,
          }}
        >
          Moderation
        </Button>
      )}
    </>
  );
}
